import { useRef, useState } from 'react'

import { useToast } from '@/components/Toast'
import { getUserFacingMessage } from '@/lib/errorMessage'
import {
  apiCheckAddressRegistered,
  apiCheckSealedItemStock,
  apiCheckServiceArea,
  apiGetSealedOrderAvailability,
} from '@/payment/api'

import { validateCtaClick } from '../lib/validation'

interface UseSealedVinylCtaParams {
  /** 검증 통과 — 신청 화면으로 */
  onPass: () => void
  /** 이미 신청한 활성 주문 존재 — 신청 정보 화면으로 */
  onAlreadyApplied: () => void
  /** 재고 소진 — 재고 소진 화면으로 */
  onSoldOut: () => void
  /** 대표 지번 주소 미등록 — 주소 등록 컨펌 모달 */
  onAddressUnregistered: () => void
  /** 등록 주소가 배송 불가 지역 — 주소 변경 컨펌 모달 */
  onServiceUnavailable: () => void
}

/**
 * 밀봉비닐 CTA "배송비만 결제하고 밀봉비닐 받기" 클릭 핸들러.
 *
 * 가능여부·재고·주소등록·서비스지역을 검증해 분기한다:
 *  - 통과              → onPass
 *  - 이미 신청한 주문   → onAlreadyApplied (신청 정보 화면으로)
 *  - 재고 소진          → onSoldOut
 *  - 주소 미등록        → onAddressUnregistered (주소 등록 컨펌 모달 → onNext my/address)
 *  - 배송 불가 지역     → onServiceUnavailable (주소 변경 컨펌 모달 → onNext my/address)
 *  - 검증 조회 실패     → 재시도 토스트
 *
 * 중복 클릭은 isPendingRef 로 즉시 차단한다 — state(isCtaPending)는 리렌더
 * 전이라 동일 이벤트 루프의 연속 클릭을 막지 못한다. state 는 버튼 disabled
 * 시각 피드백 전용.
 */
export function useSealedVinylCta({
  onPass,
  onAlreadyApplied,
  onSoldOut,
  onAddressUnregistered,
  onServiceUnavailable,
}: UseSealedVinylCtaParams) {
  const { show } = useToast()
  const [isCtaPending, setIsCtaPending] = useState(false)
  const isPendingRef = useRef(false)

  const runValidation = async () => {
    if (isPendingRef.current) return
    isPendingRef.current = true
    setIsCtaPending(true)
    try {
      const result = await validateCtaClick({
        // GET /product-orders/availability — 결제 완료 활성 주문 없음
        checkAvailability: apiGetSealedOrderAvailability,
        // GET /product-orders/item-check — 봉투 재고/판매여부 (isOnSale && stock>0)
        checkStock: apiCheckSealedItemStock,
        checkAddressRegistered: apiCheckAddressRegistered,
        // GET /orders/availability?addressJibun=... — 등록 주소가 배송 서비스 지역인지
        checkServiceArea: apiCheckServiceArea,
      })

      if (result.type === 'alreadyApplied') {
        onAlreadyApplied()
        return
      }
      if (result.type === 'soldOut') {
        onSoldOut()
        return
      }
      if (result.type === 'addressUnregistered') {
        onAddressUnregistered()
        return
      }
      if (result.type === 'serviceUnavailable') {
        onServiceUnavailable()
        return
      }
      onPass()
    } catch (error) {
      show(getUserFacingMessage(error))
    } finally {
      isPendingRef.current = false
      setIsCtaPending(false)
    }
  }

  const handleCtaClick = () => {
    void runValidation()
  }

  return { handleCtaClick, isCtaPending }
}
